// src/context/AppContext.tsx - FIXED: Remove duplicate appReducer
import { createContext } from 'react';
import type { 
  AppState, 
  AppAction, 
  AppContextType,
  DataSourceType,
  SchemaResponse
} from '../types';

const createInitialState = (): AppState => ({
  // Initialization
  initialized: false,
  initializing: false,

  // Billing
  billingBalance: null,
  billingLoading: false,
  billingError: null,
  
  // API Schema
  schema: null,
  schemaLoading: false,
  schemaError: null,
  
  // Current filter metadata
  metadata: null,
  metadataLoading: false,
  metadataError: null,
  
  // Keyword suggestions cache
  keywordSuggestions: null,
  
  // Data
  contacts: [],
  
  // Selection state
  selection: {
    selectedIds: new Set<string>(),
    selectAll: false,
    lastSelected: null
  },
  
  // UI State
  loading: false,
  error: null,
  apiConnected: false,
  
  // View State
  currentView: 'main',
  
  // Search state - preserves user context
  searchState: {
    query: '',
    page: 1,
    sorting: [],
    filters: {},
    dataSource: 'people'
  },
  
  // Results State
  totalResults: 0,
  totalPages: 0,
  pageSize: 25,
  lastSearchTime: null,
  
  // Column configuration
  visibleColumns: [],
  
  // Toast State
  toasts: [],
  
  // Confirmation Dialog State
  confirmationDialog: null,

  // Active Jobs State
  activeJobs: [],

  // SIMPLIFIED: Single navigation state
  navigationState: {
    view: 'people',
    listId: null,
  },

  // Data source counts
  dataSourceCounts: {
    people: null,
    contacts: null
  },

  // ENHANCED: Processing state for multiple concurrent jobs
  processing: {
    uuids: new Set<string>(),
    operation: null,
    jobId: null
  }
});

export const initialState: AppState = createInitialState();

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_INITIALIZING':
      return { ...state, initializing: action.payload };
    
    case 'SET_INITIALIZED':
      return { ...state, initialized: action.payload, initializing: false };
    
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_API_CONNECTED':
      return { ...state, apiConnected: action.payload };

    case 'SET_BILLING_BALANCE':
      return { ...state, billingBalance: action.payload, billingError: null };
    
    case 'SET_BILLING_LOADING':
      return { ...state, billingLoading: action.payload };
    
    case 'SET_BILLING_ERROR':
      return { ...state, billingError: action.payload, billingBalance: null };
    
    case 'SET_SCHEMA': {
      const schema = action.payload;
      let defaultColumns: string[];
      
      // Determine which columns to show based on current data source
      if (state.searchState.dataSource === 'people') {
        // For search people screen: use visible_search: true columns
        defaultColumns = schema.column_configs
          .filter(col => col.visible_search === true)
          .map(col => col.id);
      } else {
        // For lists contacts screen: use visible: true columns
        defaultColumns = schema.column_configs
          .filter(col => col.visible === true)
          .map(col => col.id);
      }
      
      return { 
        ...state, 
        schema,
        schemaLoading: false,
        schemaError: null,
        visibleColumns: defaultColumns,
        pageSize: schema.ui_config?.default_page_size || state.pageSize
      };
    }
    
    case 'SET_SCHEMA_LOADING':
      return { ...state, schemaLoading: action.payload };
    
    case 'SET_SCHEMA_ERROR':
      return { ...state, schemaError: action.payload, schemaLoading: false };
    
    case 'SET_METADATA':
      return { ...state, metadata: action.payload, metadataLoading: false, metadataError: null };
    
    case 'SET_METADATA_LOADING':
      return { ...state, metadataLoading: action.payload };
    
    case 'SET_METADATA_ERROR':
      return { ...state, metadataError: action.payload, metadataLoading: false };
    
    case 'SET_KEYWORD_SUGGESTIONS':
      return { ...state, keywordSuggestions: action.payload };
    
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchState: { ...state.searchState, query: action.payload, page: 1 },
        selection: { selectedIds: new Set<string>(), selectAll: false, lastSelected: null }
      };
    
    case 'SET_PAGE':
      return {
        ...state,
        searchState: { ...state.searchState, page: action.payload }
      };
    
    case 'SET_SORTING':
      return {
        ...state,
        searchState: { 
          ...state.searchState, 
          sorting: Array.isArray(action.payload) ? action.payload : [],
          page: 1 
        }
      };
    
    case 'SET_FILTERS': {
      return {
        ...state,
        searchState: { ...state.searchState, filters: action.payload, page: 1 },
        selection: { selectedIds: new Set<string>(), selectAll: false, lastSelected: null }
      };
    }
      
    case 'SET_DATA_SOURCE': {
      const newDataSource = action.payload;
      // Setting data source
      
      // Update visible columns based on new data source
      let newVisibleColumns: string[] = [];
      if (state.schema) {
        if (newDataSource === 'people') {
          // For search people screen: use visible_search: true columns
          newVisibleColumns = state.schema.column_configs
            .filter(col => col.visible_search === true)
            .map(col => col.id);
          // Setting people columns
        } else {
          // For lists contacts screen: use visible: true columns
          newVisibleColumns = state.schema.column_configs
            .filter(col => col.visible === true)
            .map(col => col.id);
          // Setting contacts columns
        }
      } else {
        // No schema available when setting data source
      }
      
      return {
        ...state,
        searchState: { 
          ...state.searchState, 
          dataSource: newDataSource, 
          filters: {},
          page: 1,
          query: ''
        },
        selection: { selectedIds: new Set<string>(), selectAll: false, lastSelected: null },
        navigationState: {
          view: newDataSource === 'people' ? 'people' : 'list',
          listId: null
        },
        visibleColumns: newVisibleColumns
      };
    }
    
    case 'SET_SEARCH_RESULTS': {
      const { contacts, total, totalPages, searchTime, dataSource } = action.payload;
      
      // Validate that we're setting results for the correct data source
      if (state.searchState.dataSource !== dataSource) {
        console.warn(`⚠️ Data source mismatch in search results! Current: ${state.searchState.dataSource}, Setting: ${dataSource}`);
      }
      
      return {
        ...state,
        contacts,
        totalResults: total,
        totalPages,
        lastSearchTime: searchTime
      };
    }
    
    case 'SET_VISIBLE_COLUMNS':
      return { ...state, visibleColumns: action.payload };
    
    case 'CLEAR_ALL_FILTERS': {
      return {
        ...state,
        searchState: { 
          ...state.searchState, 
          filters: {}, 
          page: 1 
        },
        selection: { selectedIds: new Set<string>(), selectAll: false, lastSelected: null }
      };
    }
    
    case 'CLEAR_FILTERS_BY_CATEGORY': {
      const { category } = action.payload;
      
      // Get fields for the specified category
      const categoryFields = getCategoryFields(state.schema, state.searchState.dataSource, category);
      
      // Create new filters object without the category fields
      const newFilters = { ...state.searchState.filters };
      categoryFields.forEach((fieldName: string) => {
        delete newFilters[fieldName];
      });
      
      return {
        ...state,
        searchState: { 
          ...state.searchState, 
          filters: newFilters, 
          page: 1 
        },
        selection: { selectedIds: new Set<string>(), selectAll: false, lastSelected: null }
      };
    }
    
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload]
      };
    
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload)
      };
    
    case 'SELECT_ITEM': {
      const newSelectedIds = new Set(state.selection.selectedIds);
      newSelectedIds.add(action.payload);
      return {
        ...state,
        selection: {
          ...state.selection,
          selectedIds: newSelectedIds,
          lastSelected: action.payload
        }
      };
    }
    
    case 'DESELECT_ITEM': {
      const newSelectedIds = new Set(state.selection.selectedIds);
      newSelectedIds.delete(action.payload);
      return {
        ...state,
        selection: {
          ...state.selection,
          selectedIds: newSelectedIds,
          selectAll: false
        }
      };
    }
    
    case 'SELECT_ALL':
      return {
        ...state,
        selection: {
          ...state.selection,
          selectAll: action.payload,
          selectedIds: action.payload ? state.selection.selectedIds : new Set<string>()
        }
      };
    
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selection: {
          selectedIds: new Set<string>(),
          selectAll: false,
          lastSelected: null
        }
      };
    
    case 'SHOW_CONFIRMATION_DIALOG':
      return {
        ...state,
        confirmationDialog: action.payload
      };
    
    case 'HIDE_CONFIRMATION_DIALOG':
      return {
        ...state,
        confirmationDialog: null
      };
    
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload
      };
    
    case 'SET_ACTIVE_JOBS':
      return { ...state, activeJobs: action.payload };

    case 'REMOVE_JOB':
      return {
        ...state,
        activeJobs: state.activeJobs.filter(job => job.job_id !== action.payload)
      };

    case 'SET_NAVIGATION':
      return {
        ...state,
        navigationState: action.payload,
        searchState: {
          ...state.searchState,
          dataSource: action.payload.view === 'people' ? 'people' : 'contacts',
          page: 1,
          filters: {},
          query: '',
        },
        selection: { selectedIds: new Set<string>(), selectAll: false, lastSelected: null },
      };

    case 'SET_DATA_SOURCE_COUNT': {
      const { dataSource, count } = action.payload;
      return {
        ...state,
        dataSourceCounts: {
          ...state.dataSourceCounts,
          [dataSource]: count
        }
      };
    }

    // ENHANCED: Processing state actions with better multi-job support
    case 'SET_PROCESSING': {
      const { uuids, operation, jobId } = action.payload;
      // Setting processing state
      return {
        ...state,
        processing: {
          uuids: new Set(uuids),
          operation,
          jobId
        }
      };
    }

    case 'CLEAR_PROCESSING':
      // Clearing processing state
      return {
        ...state,
        processing: {
          uuids: new Set<string>(),
          operation: null,
          jobId: null
        }
      };

    case 'UPDATE_VISIBLE_COLUMNS_FOR_DATA_SOURCE': {
      if (!state.schema) return state;
      
      let newVisibleColumns: string[];
      
      // Determine which columns to show based on current data source
      if (state.searchState.dataSource === 'people') {
        // For search people screen: use visible_search: true columns
        newVisibleColumns = state.schema.column_configs
          .filter(col => col.visible_search === true)
          .map(col => col.id);
      } else {
        // For lists contacts screen: use visible: true columns
        newVisibleColumns = state.schema.column_configs
          .filter(col => col.visible === true)
          .map(col => col.id);
      }
      
      return { 
        ...state, 
        visibleColumns: newVisibleColumns
      };
    }

    default:
      return state;
  }
}

// Helper function to get fields for a specific category
const getCategoryFields = (schema: SchemaResponse | null, dataSource: DataSourceType, category: string): string[] => {
  if (!schema) return [];

  const dataSourceConfig = schema.data_sources?.[dataSource];
  let categoryFields: string[] = [];
  
  if (dataSourceConfig) {
    categoryFields = dataSourceConfig.sections[category] || [];
  } else {
    categoryFields = schema.sections[category] || [];
  }

  return categoryFields.filter(fieldName => {
    const fieldDef = schema.field_definitions[fieldName];
    if (!fieldDef) return false;
    
    if (fieldDef.internal) return false;
    if (fieldDef.contacts_only && dataSource !== 'contacts') return false;
    
    return true;
  });
};

export const AppContext = createContext<AppContextType | undefined>(undefined);